import React, { useState, useEffect } from 'react';
import HomeSSR from '../../components/HomeSSR';
import HomeDiscoverSSR from '../../components/HomeDiscoverSSR';
import { HOME_REDESIGN_ENABLED } from '../../../config/homeRedesign';

/**
 * Homepage with SSR support.
 *
 * SSR: Renders the server component (static HTML for Google).
 * Client: After hydration, loads the full SPA App component
 * with search, filters, map, infinite scroll, etc.
 *
 * Same pattern as src/pages/[[catchAll]]/+Page.tsx which renders
 * a shell then lazy-loads App for SPA routes.
 *
 * HOME-01: which server component runs is the rollback flag's only job here.
 * `HOME_REDESIGN_ENABLED` is a build-time constant, so the branch is resolved
 * before the bundle ships and the server and the client cannot pick different
 * sides of it — which is the failure mode a runtime flag would have introduced
 * on exactly the page that is server-rendered.
 *
 * `HomeSSR` is untouched and stays the fallback. Flipping the constant off
 * restores the previous homepage with no other change.
 */
export default function IndexPage() {
  const [AppComponent, setAppComponent] = useState<React.ComponentType | null>(null);

  useEffect(() => {
    // After client-side hydration, load the full interactive SPA
    import('../../../App').then((mod) => {
      setAppComponent(() => mod.default);
    });
  }, []);

  // Client-side: once App is loaded, render the full SPA
  if (AppComponent) {
    return <AppComponent />;
  }

  // SSR + initial client render: show the SEO-optimized static version
  return HOME_REDESIGN_ENABLED ? <HomeDiscoverSSR /> : <HomeSSR />;
}
